import React, { Component } from 'react';
import SelectReact from 'react-select';
import { connect } from 'react-redux';
import { 
  Box,
  Divider,
  Grid,
  Modal,
  Paper,
  Snackbar,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import {
  addLocation,
  fetchLocations,
  fetchLocationTypes,
  updateLocations
} from '../../../helpers/externalRequests';
import { formatLocationTypeOptions } from '../../../helpers/optionFormatters';
import i18n from '../../../i18n';
import RequireScope from '../../RequireScope';
import SimpleDialogFragment from '../SimpleDialogFragment';
import CustomValueContainer from '../../ReactSelect/CustomValueContainer';
import CustomDropdownIndicator from '../../ReactSelect/CustomDropdownIndicator';
import { groupLocations } from '../../../helpers/helpers';
import { customSettingsSelectStyles } from '../../../assets/stylesheets/ReactSelectStyles';
import LocationUpdateModal from './LocationUpdateModal';
import { UserType } from '../../../validation';

class LocationEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedOption: null,
      allLocations: [],
      locationTypeOptions: [],
      showEditModal: false,
      showWarning: false,
      selectedLocation: null,
      openSnack: false,
      snackbarMsg: ''
    };

    this.updateLocationRef = React.createRef();
  }

  showEditModal = () => {
    const {
      selectedOption
    } = this.state;
    if (!selectedOption) {
      this.setState({ showWarning: true })
    } else {
      this.setState({ showEditModal: true });
    }
  };

  hideEditModal = () => {
    const { selectedOption } = this.state;
    this.setState({ showEditModal: false }, () => {
      if (this.updateLocationRef.current && selectedOption) {
        this.updateLocationRef.current.refreshState();
      }
    });
  };

  showAddModal = () => {
    if (this.updateLocationRef.current) {
      this.updateLocationRef.current.clearState();
    }
    this.setState({ showEditModal: true, selectedOption: null, selectedLocation: null });
  };

  hideAddModal = () => {
    this.setState({ showEditModal: false });
  };

  componentDidMount = () => {
    this.getLocations();
    this.getLocationTypes();
  };

  onLocationChange = event => {
    this.setState(prevState => ({
      selectedLocation: prevState.allLocations.find(
        location => location.id === event.value
      ),
      selectedOption: event,
    }), () => {
      if (this.updateLocationRef.current) {
        this.updateLocationRef.current.refreshState();
      }
    });
  };

  getLocations = async () => {
    const allLocations = await fetchLocations();
    const locationTypes = [];
    // extract location types from existing locations
    allLocations.data.forEach(location => {
      if (!locationTypes.map(locationType => locationType.id).includes(location.locationTypeId)) {
        locationTypes.push(location.locationType);
      };
    });
    const groupedLocations = groupLocations(locationTypes, allLocations.data);
    this.setState({
      allLocations: allLocations.data,
      groupedLocations,
    });
  };

  getLocationTypes = async () => {
    const allLocationTypes = await fetchLocationTypes();
    const locationTypeOptions = formatLocationTypeOptions(allLocationTypes.data);
    this.setState({ locationTypeOptions });
  }

  addNewLocation = async (params) => {
    const {
      user,
    } = this.props;
    const updatedParams = {
      ...params,
      userEmail: user.email,
    };
    try {
      await addLocation(updatedParams);
      await this.getLocations();
      this.setState({
        openSnack: true,
        showEditModal: false,
        snackbarMsg: i18n.t('setting:addSuccess'),
      });
    } catch (error) {
      this.setState({
        openSnack: true,
        showEditModal: false,
        snackbarMsg: `${i18n.t('setting:addErrorMsg')} ${error}`,
      });
    }
  };

  editLocation = (locationId, locationName) => {
    updateLocations({ locationId, locationName })
    .then(async () => {
      await this.getLocations();
      this.setState({
        openSnack: true,
        selectedOption: null,
        showEditModal: false,
        snackbarMsg: i18n.t('setting:editSuccess')
      });
    })
    .catch((error) => {
      this.setState({
        openSnack: true,
        showEditModal: false,
        snackbarMsg: `${i18n.t('setting:editErrorMsg')} ${error}`
      });
    });
  };

  editLocationV2 = async (params) => {
    try {
      await updateLocations(params);
      await this.getLocations();
      this.setState({
        openSnack: true,
        selectedOption: null,
        showEditModal: false,
        snackbarMsg: i18n.t('setting:editSuccess')
      });
    } catch (error) {
      this.setState({
        openSnack: true,
        showEditModal: false,
        snackbarMsg: `${i18n.t('setting:editErrorMsg')} ${error}`
      });
    }
  }

  hideWarning = () => {
    this.setState({
      showWarning: false,
    })
  }

  closeSnackBar = () => {
    this.setState({ openSnack: false, snackbarMsg: '' });
  };

  render() {
    const {
      allLocations,
      groupedLocations,
      selectedOption,
      showEditModal,
      showWarning,
      selectedLocation,
      openSnack,
      snackbarMsg,
      locationTypeOptions,
    } = this.state;

    const customComponents = {
      DropdownIndicator: CustomDropdownIndicator,
      IndicatorSeparator: null,
      ValueContainer: CustomValueContainer
    };

    return (
      <Grid container>
        <Grid item xs={7}>
          <h6>{i18n.t('setting:manageLocations')}</h6>
          <Divider />
          <br />
          <Box className="settings-box">
            <SelectReact
              fullWidth
              styles={customSettingsSelectStyles}
              components={customComponents}
              hideSelectedOptions
              value={selectedOption}
              placeholder={i18n.t('setting:chooseLocation')}
              menuShouldScrollIntoView={false}
              className="settings-select-field"
              options={groupedLocations}
              onChange={this.onLocationChange}
            />
            <Typography className="settings-in-box-typography" variant="caption" display="block" gutterBottom>{i18n.t('setting:settingsHelp')}</Typography>
          </Box>
          <RequireScope scopes={['entities:write']}>
            <IconButton
              aria-label="add"
              color="secondary"
              onClick={this.showAddModal}
            >
              <AddIcon />
            </IconButton>
          </RequireScope>
          <RequireScope scopes={['entities:write']}>
            <IconButton
              aria-label="edit"
              color="secondary"
              onClick={this.showEditModal}
            >
              <EditIcon />
            </IconButton>
          </RequireScope>
          
        </Grid>
        <LocationUpdateModal
          ref={this.updateLocationRef}
          showEditModal={showEditModal}
          hideEditModal={this.hideEditModal}
          location={selectedLocation}
          allLocations={allLocations}
          locationTypeOptions={locationTypeOptions}
          updateLocation={this.editLocationV2}
          addLocation={this.addNewLocation}
        />
        <Modal open={showWarning} onClose={this.hideWarning}>
          <Paper className="editModal">
            <SimpleDialogFragment
              title={i18n.t('setting:warningModal')}
              buttonLabel={i18n.t('close')}
              onClick={this.hideWarning}
            />
          </Paper>
        </Modal>
        <Snackbar
          message={snackbarMsg}
          open={openSnack}
          onClose={this.closeSnackBar}
          autoHideDuration={2000}
        />
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user,
});

LocationEditor.propTypes = {
  user: UserType.isRequired,
}

export { LocationEditor as LocationEditorNoLDConsumer };
export default connect(mapStateToProps)(LocationEditor);
